import { WEAPON_TYPES } from '../core/config.js';
import { metaProgression } from '../systems/MetaProgression.js';

export class HUDManager {
    constructor(gameState) {
        this.gameState = gameState;
        this.elements = this.getHUDElements();
        this.updateInterval = null;
        this.lastFragmentCount = 0;
        
        // Start update loop
        this.startUpdateLoop();
    }
    
    getHUDElements() {
        return {
            // Health elements
            healthValue: document.getElementById('healthValue'),
            maxHealthValue: document.getElementById('maxHealthValue'),
            healthFill: document.getElementById('healthFill'),
            
            // Level and experience
            levelValue: document.getElementById('levelValue'),
            expValue: document.getElementById('expValue'),
            expMax: document.getElementById('expMax'),
            expFill: document.getElementById('expFill'),
            
            // Time and stats
            timeValue: document.getElementById('timeValue'),
            systemTime: document.getElementById('systemTime'),
            enemyCount: document.getElementById('enemyCount'),
            killCount: document.getElementById('killCount'),
            
            // Fragment tracking
            fragmentCount: document.getElementById('fragmentCount'),
            fragmentDisplay: document.getElementById('fragmentDisplay'),
            
            // Weapon info
            weaponName: document.getElementById('weaponName'),
            weaponDamage: document.getElementById('weaponDamage'),
            weaponRate: document.getElementById('weaponRate')
        };
    }
    
    startUpdateLoop() {
        // Update at 30 FPS to reduce overhead
        this.updateInterval = setInterval(() => {
            this.update();
        }, 1000 / 30);
    }
    
    stopUpdateLoop() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
    }
    
    update() {
        if (!this.gameState) return;
        
        this.updateHealth();
        this.updateLevel();
        this.updateExperience();
        this.updateTime();
        this.updateCounts();
        this.updateWeaponInfo();
        this.updateFragmentTracking();
    }
    
    updateHealth() {
        const health = Math.ceil(this.gameState.stats.health);
        const maxHealth = this.gameState.stats.maxHealth;
        
        if (this.elements.healthValue) {
            this.elements.healthValue.textContent = health;
        }
        if (this.elements.maxHealthValue) {
            this.elements.maxHealthValue.textContent = maxHealth;
        }
        if (this.elements.healthFill) {
            const percentage = Math.max(0, (health / maxHealth) * 100);
            this.elements.healthFill.style.width = `${percentage}%`;
            
            // Change color based on health percentage
            if (percentage < 25) {
                this.elements.healthFill.style.background = 'linear-gradient(90deg, #ff1111, #ff3333, #ff1111)';
            } else if (percentage < 50) {
                this.elements.healthFill.style.background = 'linear-gradient(90deg, #ff6611, #ff8833, #ff6611)';
            } else {
                this.elements.healthFill.style.background = 'linear-gradient(90deg, #ff3366, #ff6699, #ff3366)';
            }
        }
    }
    
    updateLevel() {
        if (this.elements.levelValue) {
            this.elements.levelValue.textContent = this.gameState.stats.level;
        }
    }
    
    updateExperience() {
        const exp = Math.floor(this.gameState.stats.experience);
        const expMax = this.gameState.stats.experienceToNext;
        
        if (this.elements.expValue) {
            this.elements.expValue.textContent = exp;
        }
        if (this.elements.expMax) {
            this.elements.expMax.textContent = expMax;
        }
        if (this.elements.expFill) {
            const percentage = Math.max(0, (exp / expMax) * 100);
            this.elements.expFill.style.width = `${percentage}%`;
        }
    }
    
    updateTime() {
        const totalSeconds = this.gameState.stats.time;
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = Math.floor(totalSeconds % 60);
        
        if (this.elements.timeValue) {
            this.elements.timeValue.textContent = 
                `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        
        // Update system time (real-world clock)
        if (this.elements.systemTime) {
            const now = new Date();
            const timeString = now.toLocaleTimeString('en-US', { 
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
            this.elements.systemTime.textContent = `SYSTEM TIME: ${timeString}`;
        }
    }
    
    updateCounts() {
        if (this.elements.enemyCount) {
            this.elements.enemyCount.textContent = this.gameState.enemies.length;
        }
        if (this.elements.killCount) {
            this.elements.killCount.textContent = this.gameState.stats.kills;
        }
        
        // Show enemy bullets if there are any
        const enemyBullets = this.gameState.enemyBullets?.length || 0;
        if (enemyBullets > 0) {
            if (!document.getElementById('enemyBulletCount')) {
                const bulletCountEl = document.createElement('div');
                bulletCountEl.id = 'enemyBulletCount';
                bulletCountEl.className = 'stat-item';
                bulletCountEl.innerHTML = '◆ HOSTILE: <span id="bulletCountValue">0</span>';
                bulletCountEl.style.color = '#ff8888';
                
                // Add it to the second stat row
                const statRows = document.querySelectorAll('.stat-row');
                if (statRows.length > 1) {
                    statRows[1].appendChild(bulletCountEl);
                }
            }
            const bulletCountValue = document.getElementById('bulletCountValue');
            if (bulletCountValue) {
                bulletCountValue.textContent = enemyBullets;
            }
        } else {
            // Remove enemy bullet counter if no bullets
            const bulletCountEl = document.getElementById('enemyBulletCount');
            if (bulletCountEl) {
                bulletCountEl.remove();
            }
        }
    }
    
    updateWeaponInfo() {
        const player = this.gameState.player;
        if (!player) return;
        
        // Show active weapons count instead of single weapon
        if (this.elements.weaponName) {
            const activeCount = player.activeWeapons.size;
            const maxSlots = player.weaponSlots;
            this.elements.weaponName.textContent = `Active Weapons: ${activeCount}/${maxSlots}`;
        }
        
        // Show total damage from all weapons
        if (this.elements.weaponDamage) {
            let totalDamage = 0;
            for (const [weaponType, weaponData] of player.activeWeapons) {
                const weapon = WEAPON_TYPES[weaponType];
                const levelMultiplier = 1 + (weaponData.level - 1) * 0.25;
                totalDamage += weapon.damage * levelMultiplier;
            }
            this.elements.weaponDamage.textContent = Math.floor(totalDamage);
        }
        
        // Show average fire rate
        if (this.elements.weaponRate) {
            let totalRate = 0;
            for (const [weaponType, weaponData] of player.activeWeapons) {
                const weapon = WEAPON_TYPES[weaponType];
                const fireRate = weapon.fireRate * (1 + weaponData.level * 0.1);
                totalRate += fireRate;
            }
            this.elements.weaponRate.textContent = totalRate.toFixed(1);
        }
    }
    
    updateFragmentTracking() {
        // Track Data Fragments earned this run
        if (metaProgression.currentRunStats) {
            const currentFragments = metaProgression.currentRunStats.fragmentsEarned || 0;
            
            if (this.elements.fragmentCount) {
                this.elements.fragmentCount.textContent = currentFragments;
            }
            
            // Show animation when fragments increase
            if (currentFragments > this.lastFragmentCount) {
                this.showFragmentGain(currentFragments - this.lastFragmentCount);
                this.lastFragmentCount = currentFragments;
            }
        }
    }
    
    showMessage(message, duration = 2000, className = '') {
        const messageEl = document.createElement('div');
        messageEl.className = `hud-message ${className}`;
        messageEl.textContent = message;
        
        // Enhanced styling for different message types
        let color = '#00ff88';
        let shadowColor = 'rgba(0, 255, 136, 0.5)';
        
        if (className.includes('boss')) {
            color = '#ff4444';
            shadowColor = 'rgba(255, 68, 68, 0.8)';
        } else if (className.includes('level')) {
            color = '#ffaa00';
            shadowColor = 'rgba(255, 170, 0, 0.8)';
        } else if (className.includes('achievement')) {
            color = '#ffff00';
            shadowColor = 'rgba(255, 255, 0, 0.8)';
        }
        
        messageEl.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: ${color};
            font-size: 20px;
            font-weight: 700;
            text-shadow: 0 0 20px ${color}, 0 0 40px ${shadowColor};
            pointer-events: none;
            z-index: 160;
            animation: enhancedMessageSlide 0.4s ease-out;
            font-family: var(--font-mono);
            text-align: center;
            max-width: 80vw;
            line-height: 1.2;
        `;
        
        document.getElementById('gameContainer').appendChild(messageEl);
        
        setTimeout(() => {
            if (messageEl.parentNode) {
                messageEl.style.animation = 'enhancedMessageSlideOut 0.4s ease-in forwards';
                setTimeout(() => {
                    if (messageEl.parentNode) messageEl.parentNode.removeChild(messageEl);
                }, 400);
            }
        }, duration);
    }
    
    showFragmentGain(amount) {
        const fragmentGainEl = document.createElement('div');
        fragmentGainEl.className = 'fragment-gain-effect';
        fragmentGainEl.textContent = `+${amount} FRAGMENTS`;
        fragmentGainEl.style.cssText = `
            position: absolute;
            top: 20px;
            right: 20px;
            color: #ffaa00;
            font-size: 14px;
            font-weight: 700;
            text-shadow: 0 0 15px #ffaa00;
            pointer-events: none;
            z-index: 150;
            animation: fragmentGainFloat 2s ease-out forwards;
            font-family: var(--font-mono);
        `;
        
        document.getElementById('gameContainer').appendChild(fragmentGainEl);
        
        setTimeout(() => {
            if (fragmentGainEl.parentNode) fragmentGainEl.parentNode.removeChild(fragmentGainEl);
        }, 2000);
    }
    
    showThreatWarning(threatLevel = 'high') {
        const warningEl = document.createElement('div');
        warningEl.className = 'threat-warning';
        
        let message = '⚠ THREAT LEVEL ELEVATED ⚠';
        let color = '#ffaa00';
        
        if (threatLevel === 'critical') {
            message = '⚠⚠ CRITICAL THREAT DETECTED ⚠⚠';
            color = '#ff4444';
        } else if (threatLevel === 'boss') {
            message = '💀 BOSS ENTITY SPAWNED 💀';
            color = '#ff00ff';
        }
        
        warningEl.textContent = message;
        warningEl.style.cssText = `
            position: absolute;
            top: 30%;
            left: 50%;
            transform: translateX(-50%);
            color: ${color};
            font-size: 18px;
            font-weight: 700;
            text-shadow: 0 0 25px ${color};
            pointer-events: none;
            z-index: 180;
            animation: threatWarningPulse 0.5s ease-in-out 4;
            font-family: var(--font-mono);
            text-align: center;
        `;
        
        document.getElementById('gameContainer').appendChild(warningEl);
        
        setTimeout(() => {
            if (warningEl.parentNode) warningEl.parentNode.removeChild(warningEl);
        }, 2000);
    }
    
    showDifficultyIncrease(multiplier) {
        const difficultyEl = document.createElement('div');
        difficultyEl.className = 'difficulty-increase';
        difficultyEl.textContent = `DIFFICULTY INCREASED: ${multiplier.toFixed(1)}x`;
        difficultyEl.style.cssText = `
            position: absolute;
            bottom: 150px;
            left: 50%;
            transform: translateX(-50%);
            color: #ff8800;
            font-size: 16px;
            font-weight: 700;
            text-shadow: 0 0 20px #ff8800;
            pointer-events: none;
            z-index: 170;
            animation: difficultySlide 3s ease-out forwards;
            font-family: var(--font-mono);
        `;
        
        document.getElementById('gameContainer').appendChild(difficultyEl);
        
        setTimeout(() => {
            if (difficultyEl.parentNode) difficultyEl.parentNode.removeChild(difficultyEl);
        }, 3000);
    }
    
    // Clean up when game ends
    destroy() {
        this.stopUpdateLoop();
        this.gameState = null;
        this.elements = null;
    }
}

// Add additional CSS animations for new effects
if (!document.getElementById('hudAnimations')) {
    const style = document.createElement('style');
    style.id = 'hudAnimations';
    style.textContent = `
        @keyframes enhancedMessageSlide {
            0% { 
                opacity: 0; 
                transform: translate(-50%, -60%) scale(0.8) rotate(-2deg); 
            }
            30% {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1.1) rotate(1deg);
            }
            100% { 
                opacity: 1; 
                transform: translate(-50%, -50%) scale(1) rotate(0deg); 
            }
        }
        
        @keyframes enhancedMessageSlideOut {
            0% { 
                opacity: 1; 
                transform: translate(-50%, -50%) scale(1); 
            }
            100% { 
                opacity: 0; 
                transform: translate(-50%, -40%) scale(0.9); 
            }
        }
        
        @keyframes fragmentGainFloat {
            0% { 
                opacity: 1; 
                transform: translateY(0) scale(1.2); 
            }
            20% {
                transform: translateY(-10px) scale(1.1);
            }
            100% { 
                opacity: 0; 
                transform: translateY(-60px) scale(0.8); 
            }
        }
        
        @keyframes threatWarningPulse {
            0%, 100% { 
                opacity: 1; 
                transform: translateX(-50%) scale(1); 
            }
            50% { 
                opacity: 0.7; 
                transform: translateX(-50%) scale(1.05); 
            }
        }
        
        @keyframes difficultySlide {
            0% { 
                opacity: 0; 
                transform: translateX(-50%) translateY(20px); 
            }
            20% { 
                opacity: 1; 
                transform: translateX(-50%) translateY(0); 
            }
            80% { 
                opacity: 1; 
                transform: translateX(-50%) translateY(0); 
            }
            100% { 
                opacity: 0; 
                transform: translateX(-50%) translateY(-20px); 
            }
        }
    `;
    document.head.appendChild(style);
} 